import { Router } from '@angular/router';
import { AuthService } from './service/auth/auth.service';
import { isCapacitorNative } from './service/platform/capacitor-platform';
import { RestService } from './service/rest/rest.service';
import { LocalDatabaseService } from './service/storage/local-database.service';
import { ContentPackService } from './service/content-pack/content-pack.service';
import { ProgressSyncService } from './service/sync/progress-sync.service';
import { LoggerService } from './service/utils/logger.service';

/**
 * Dev/E2E only deep links (native / Maestro):
 * - atplane://e2e-login?user=bonk&password=...
 * - atplane://e2e-nav?path=/dashboard/subscriptions
 * - atplane://e2e-start-test?courseId=1&subjectId=1&mode=study&amount=5
 * - atplane://e2e-sync-enqueue?questionId=1
 * - atplane://e2e-sync-status?flush=true&waitMs=15000
 * - atplane://e2e-pack-clear
 * - atplane://e2e-pack-download?courseId=1&subjectId=1
 * - atplane://e2e-pack-status?courseId=1&subjectId=1
 */
export function setupE2eDeepLink(
  auth: AuthService,
  router: Router,
  rest: RestService,
  localDb: LocalDatabaseService,
  progressSync: ProgressSyncService,
  contentPacks: ContentPackService
): () => Promise<void> {
  return async () => {
    if (!isCapacitorNative()) return;
    try {
      const { App } = await import('@capacitor/app');
      const showStatus = (text: string) => {
        let el = document.getElementById('atplane-e2e-status');
        if (!el) {
          el = document.createElement('button');
          el.id = 'atplane-e2e-status';
          el.setAttribute('type', 'button');
          el.setAttribute('role', 'status');
          el.style.position = 'fixed';
          el.style.top = '96px';
          el.style.left = '12px';
          el.style.zIndex = '2147483647';
          el.style.padding = '10px 14px';
          el.style.background = '#12324a';
          el.style.color = '#fff';
          el.style.font = '16px sans-serif';
          el.style.borderRadius = '8px';
          document.body.appendChild(el);
        }
        el.textContent = text;
        el.setAttribute('aria-label', text);
      };
      const wait = (ms: number) => new Promise((resolve) => window.setTimeout(resolve, ms));
      const pendingAfterWait = async (waitMs: number) => {
        const deadline = Date.now() + waitMs;
        let pending = await progressSync.pendingCount();
        while (pending > 0 && Date.now() < deadline) {
          await wait(500);
          pending = await progressSync.pendingCount();
        }
        return pending;
      };
      const handle = async (url: string) => {
        let parsed: URL;
        try {
          parsed = new URL(url);
        } catch {
          return;
        }
        if (parsed.protocol !== 'atplane:') {
          return;
        }
        try {
        if (parsed.hostname === 'e2e-login') {
          const user = parsed.searchParams.get('user') ?? parsed.searchParams.get('email');
          const password = decodeURIComponent(parsed.searchParams.get('password') ?? '');
          if (!user || !password) {
            LoggerService.error('[e2e-deep-link] missing user or password', null);
            return;
          }
          LoggerService.log('[e2e-deep-link] logging in', { user });
          const ok = await auth.authenticate(user, password);
          if (!ok) {
            LoggerService.error('[e2e-deep-link] login failed', null);
            return;
          }
          const loggedIn = auth.getUser();
          if (loggedIn?.admin) {
            LoggerService.error('[e2e-deep-link] admin login blocked on mobile', null);
            await auth.logoutUser();
            return;
          }
          await router.navigate(['/dashboard']);
          return;
        }
        if (parsed.hostname === 'e2e-nav') {
          const raw = decodeURIComponent(parsed.searchParams.get('path') ?? '/dashboard');
          const normalized = raw.startsWith('/') ? raw : `/${raw}`;
          // Capacitor build uses baseHref `/`, web build uses `/atplane/`.
          // Strip a leading `/atplane` or legacy `/qtrainer` if present;
          // never add it on native, where `/atplane/...` would 404 on the `**` route.
          const withoutBase = normalized.startsWith('/atplane/')
            ? normalized.substring('/atplane'.length)
            : normalized === '/atplane'
              ? '/'
              : normalized.startsWith('/qtrainer/')
                ? normalized.substring('/qtrainer'.length)
                : normalized === '/qtrainer'
                  ? '/'
                  : normalized;
          LoggerService.log('[e2e-deep-link] navigate', { path: withoutBase });
          await router.navigateByUrl(withoutBase);
          return;
        }
        if (parsed.hostname === 'e2e-start-test') {
          const user = auth.getUser();
          const sessionId = user?.sid;
          if (!sessionId) {
            showStatus('E2E start-test failed: no session');
            return;
          }
          const courseId = Number(parsed.searchParams.get('courseId') ?? '1');
          const subjectId = Number(parsed.searchParams.get('subjectId') ?? '1');
          const amount = Number(parsed.searchParams.get('amount') ?? '5');
          const uiMode = parsed.searchParams.get('mode') === 'exam' ? 'exam' : 'study';
          const apiMode = uiMode === 'exam' ? 'exam' : 'test';
          const count = await rest.loadQuestionsMeta(sessionId, courseId, String(subjectId));
          rest.setMaxQuestions(Math.max(1, Math.min(amount, count || amount)));
          let questionIds = rest.getQuestionIds().slice(0, Math.max(1, amount));
          if (questionIds.length === 0) {
            showStatus('E2E start-test failed: no questions');
            return;
          }
          const userTestId = await rest.createUserTest(
            sessionId,
            courseId,
            [subjectId],
            apiMode,
            questionIds,
            { amountQuestions: questionIds.length, excludeHidden: true }
          );
          if (!userTestId) {
            showStatus('E2E start-test failed: create test');
            return;
          }
          rest.currentUserTestId = userTestId;
          rest.currentUserTestMode = apiMode;
          const savedQuestionIds = [...questionIds];
          rest.reset();
          rest.setQuestionIdsForResume(savedQuestionIds);
          const firstQuestionId = savedQuestionIds[0];
          showStatus(`E2E start-test ready: ${userTestId}`);
          await router.navigate(
            uiMode === 'exam'
              ? ['/question-pool-exam', String(firstQuestionId), '0']
              : ['/question-pool-study', String(firstQuestionId)]
          );
          return;
        }
        if (parsed.hostname === 'e2e-sync-enqueue') {
          const user = auth.getUser();
          const userId = user?.id;
          const questionId = Number(parsed.searchParams.get('questionId') ?? '1');
          if (!userId || !Number.isFinite(questionId) || questionId <= 0) {
            showStatus('E2E sync enqueue failed');
            return;
          }
          await localDb.init();
          await localDb.insertPendingEvent(
            crypto.randomUUID(),
            'QUESTION_SEEN',
            JSON.stringify({ userId, questionId })
          );
          await progressSync.pendingCount();
          showStatus('E2E sync queued');
          return;
        }
        if (parsed.hostname === 'e2e-sync-status') {
          const flush = parsed.searchParams.get('flush') === 'true';
          const waitMs = Number(parsed.searchParams.get('waitMs') ?? '0');
          if (flush) {
            await progressSync.flush('e2e-status');
          }
          const pending = Number.isFinite(waitMs) && waitMs > 0
            ? await pendingAfterWait(waitMs)
            : await progressSync.pendingCount();
          LoggerService.log('[e2e-deep-link] sync status', { pending, flush, waitMs });
          showStatus(`E2E sync flushed: ${pending}`);
          return;
        }
        if (parsed.hostname === 'e2e-pack-clear') {
          contentPacks.setAutoDownloadEnabled(true);
          await contentPacks.clearDownloadedPacks();
          showStatus('E2E packs cleared');
          return;
        }
        if (parsed.hostname === 'e2e-pack-download') {
          const courseId = Number(parsed.searchParams.get('courseId') ?? '1');
          const subjectId = Number(parsed.searchParams.get('subjectId') ?? '1');
          contentPacks.setAutoDownloadEnabled(true);
          const ok = await contentPacks.ensureSubjectPack(courseId, subjectId);
          showStatus(ok ? 'E2E pack downloaded' : 'E2E pack download failed');
          return;
        }
        if (parsed.hostname === 'e2e-pack-status') {
          const courseId = Number(parsed.searchParams.get('courseId') ?? '1');
          const subjectId = Number(parsed.searchParams.get('subjectId') ?? '1');
          const hasPack = await localDb.hasInstalledPack(courseId, subjectId);
          const ids = await localDb.listLocalQuestionIds(courseId, [subjectId]);
          const download = await localDb.getPackDownloadState(courseId, subjectId);
          const state = download?.state ?? 'missing';
          showStatus(hasPack ? `E2E pack ${state}: ${ids.length}` : `E2E pack ${state}: 0`);
          return;
        }
        if (parsed.hostname === 'e2e-pack-local-question') {
          const courseId = Number(parsed.searchParams.get('courseId') ?? '1');
          const subjectId = Number(parsed.searchParams.get('subjectId') ?? '1');
          const ids = await localDb.listLocalQuestionIds(courseId, [subjectId]);
          const questions = ids.length > 0
            ? await localDb.getLocalQuestions(courseId, [subjectId], [ids[0]])
            : [];
          showStatus(questions.length > 0 ? `E2E local question loaded: ${ids[0]}` : 'E2E local question failed');
          return;
        }
        } catch (err) {
          LoggerService.error('[e2e-deep-link] handler failed', err);
          showStatus('E2E deep link error');
        }
      };
      (window as unknown as { __atplaneE2eOpen?: (url: string) => void }).__atplaneE2eOpen = (url) => {
        void handle(url);
      };
      let pendingUrl: string | null = null;
      await App.addListener('appUrlOpen', (event) => {
        pendingUrl = event.url;
        void handle(event.url);
      });
      const launch = await App.getLaunchUrl();
      if (launch?.url) {
        pendingUrl = launch.url;
        await handle(launch.url);
      }
      // Maestro openLink can arrive before the listener is attached on cold start.
      window.setTimeout(() => {
        if (pendingUrl) {
          void handle(pendingUrl);
        }
      }, 1500);
    } catch (e) {
      LoggerService.error('[e2e-deep-link] App plugin unavailable', e);
    }
  };
}
